// frontend/src/pages/LeadsPage.js
// v2.0 - Added search, status filter, and edit modal with full CRUD

import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import apiService, { razobratSpisokObrashcheniy } from '../services/apiService';
import LoadingSpinner from '../components/LoadingSpinner';
import './LeadsPage.css';
import TeamAssignmentModal from '../components/Leads/TeamAssignmentModal';
import FitenkoObrashcheniya from './FitenkoObrashcheniya';
import { useBranding } from '../context/BrandingContext';
import { displayEmail, sourceMeta } from '../config/istochnikiObrashcheniy';

const STATUSES = ['ALL', 'NEW', 'CONTACTED', 'QUALIFIED', 'UNQUALIFIED', 'CONVERTED'];

// 🔴 `displayEmail` и `sourceMeta` УЕХАЛИ в `config/istochnikiObrashcheniy.js`
//    (quick-260911-27v): потребителей у них стало ДВА — эта страница и экран
//    обращений fitenko. Копия в двух экранах была бы двумя держателями одного
//    ответа, а ввоз из нового экрана в старый замкнул бы кольцо (старый УЖЕ
//    ввозит новый). Тела функций перенесены ДОСЛОВНО, буква в букву.

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        return new Date(dateString).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
    } catch { return dateString; }
};

// --- Preview Modal ---
const LeadPreviewModal = ({ lead, onClose }) => {
    if (!lead) return null;
    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Lead Details</h2>
                    <button onClick={onClose} className="modal-close-btn">&times;</button>
                </div>
                <div className="modal-body">
                    <p><strong>Full Name:</strong> {lead.full_name}</p>
                    <p><strong>Email:</strong> {displayEmail(lead.email)}</p>
                    <p><strong>Phone:</strong> {lead.phone || 'Not provided'}</p>
                    <p><strong>Source:</strong> {lead.source}</p>
                    <p><strong>Status:</strong> <span className={`status-badge status-${lead.status.toLowerCase()}`}>{lead.status}</span></p>
                    <p><strong>Created At:</strong> {formatDate(lead.created_at)}</p>
                    <hr />
                    <p><strong>Notes:</strong></p>
                    <p>{lead.notes || 'N/A'}</p>
                    {lead.appointment_datetime && (
                        <div className="consultation-details-block" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                            <h4 style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Scheduled Consultation</h4>
                            <p><strong>Date & Time:</strong> {formatDate(lead.appointment_datetime)}</p>
                            {lead.appointment_gcal_link && (
                                <p>
                                    <strong>Calendar Link:</strong>{' '}
                                    <a href={lead.appointment_gcal_link} target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc', textDecoration: 'underline' }}>
                                        View Google Event
                                    </a>
                                </p>
                            )}
                        </div>
                    )}
                </div>
                <div className="modal-footer">
                    <button onClick={onClose} className="btn btn-secondary">Close</button>
                </div>
            </div>
        </div>
    );
};

// --- Edit Modal ---
// 🔴 ВЫВЕЗЕНО НАРУЖУ (quick-260911-27v), А НЕ СКОПИРОВАНО. Карточке обращения
//    `/leads/:id` нужна ТА ЖЕ форма правки (Д-04). Второй формы не заведено:
//    две формы правки одного предмета разошлись бы полями на первой же правке
//    модели, и увидел бы это только человек на экране. Кольца ввоза нет —
//    `LeadHistoryPage` этот модуль ввозит, а этот его не ввозит.
export const LeadEditModal = ({ lead, onClose, onSave }) => {
    const [form, setForm] = useState({
        full_name: lead.full_name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        source: lead.source || '',
        status: lead.status || 'NEW',
        notes: lead.notes || '',
    });
    const [saving, setSaving] = useState(false);

    const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const updated = await apiService.updateLead(lead.id, form);
            onSave(updated);
            toast.success('Lead updated successfully.');
            onClose();
        } catch (err) {
            toast.error(`Failed to update: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Edit Lead</h2>
                    <button onClick={onClose} className="modal-close-btn">&times;</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="modal-body" style={{ display: 'grid', gap: '0.75rem' }}>
                        <label>
                            <span style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Full Name</span>
                            <input className="form-input" name="full_name" value={form.full_name} onChange={handleChange} required />
                        </label>
                        <label>
                            <span style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Email</span>
                            <input className="form-input" name="email" type="email" value={form.email} onChange={handleChange} />
                        </label>
                        <label>
                            <span style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Phone</span>
                            <input className="form-input" name="phone" value={form.phone} onChange={handleChange} />
                        </label>
                        <label>
                            <span style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Source</span>
                            <input className="form-input" name="source" value={form.source} onChange={handleChange} />
                        </label>
                        <label>
                            <span style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Status</span>
                            <select className="form-input" name="status" value={form.status} onChange={handleChange}>
                                {['NEW', 'CONTACTED', 'QUALIFIED', 'UNQUALIFIED', 'CONVERTED'].map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <span style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Notes</span>
                            <textarea className="form-input" name="notes" value={form.notes} onChange={handleChange} rows={4} />
                        </label>
                    </div>
                    <div className="modal-footer">
                        <button type="button" onClick={onClose} className="btn btn-secondary" disabled={saving}>Cancel</button>
                        <button type="submit" className="btn btn-primary" disabled={saving}>
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Main Page ---
// 🔴 РУБЕЖ АРЕНДАТОРА СТОИТ В РАЗМЕТКЕ, А МАРШРУТ ОСТАЁТСЯ ОБЩИМ (Д-10).
//    `/leads` объявлен в `App.js:117` без гейта, и пункт `leads` виден всем
//    четверым арендаторам. Образец рядом: `HistoryPage` гейтит СОДЕРЖИМОЕ,
//    оставляя маршрут общим. У mlg, eln и elfny ниже идёт РОВНО ТА ЖЕ разметка,
//    что была: ни одного изменённого узла, кроме чтения новой формы ответа.
const LeadsPage = () => {
    const { isFitenko } = useBranding();
    const [leads, setLeads] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedLead, setSelectedLead] = useState(null);
    const [editingLead, setEditingLead] = useState(null);
    const [leadToAssign, setLeadToAssign] = useState(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    // ТРЕТЬЕ СОСТОЯНИЕ: ответ не той формы — это ОТКАЗ ИЗМЕРИТЕЛЯ, и он говорит
    // словами. Пустая таблица сказала бы «обращений нет» — это была бы неправда.
    const [otkazOtveta, setOtkazOtveta] = useState(null);

    useEffect(() => {
        const fetchLeads = async () => {
            try {
                // 🔴 Доводы НЕ ПЕРЕДАЮТСЯ: умолчание ручки (skip=0, limit=100) —
                //    ровно то, что эта страница получала до смены формы ответа.
                //    Прежняя разметка mlg/eln/elfny обязана показать ТО ЖЕ.
                const razbor = razobratSpisokObrashcheniy(await apiService.getLeads());
                if (razbor.rod === 'otkaz') {
                    setOtkazOtveta(razbor.tekst);
                    setLeads([]);
                    return;
                }
                setOtkazOtveta(null);
                setLeads(razbor.items);
            } catch (error) {
                toast.error(`Failed to fetch leads: ${error.message}`);
            } finally {
                setIsLoading(false);
            }
        };
        fetchLeads();
    }, []);

    const filteredLeads = useMemo(() => {
        const q = search.toLowerCase();
        return leads.filter(lead => {
            const matchesSearch = !q ||
                lead.full_name?.toLowerCase().includes(q) ||
                lead.email?.toLowerCase().includes(q) ||
                lead.phone?.toLowerCase().includes(q);
            const matchesStatus = statusFilter === 'ALL' || lead.status === statusFilter;
            return matchesSearch && matchesStatus;
        });
    }, [leads, search, statusFilter]);

    const handleConvert = async (leadId) => {
        try {
            const response = await apiService.convertLead(leadId);
            toast.success(response.message || 'Lead converted successfully!');
            setLeads(prev => prev.map(l =>
                l.id === leadId ? { ...l, status: 'CONVERTED', case_id: response.case_id } : l
            ));
        } catch (error) {
            toast.error(`Conversion failed: ${error.message}`);
        }
    };

    const handleDelete = async (leadId, leadName) => {
        if (!window.confirm(`Delete lead "${leadName}"? This cannot be undone.`)) return;
        try {
            await apiService.deleteLead(leadId);
            toast.success(`Lead "${leadName}" deleted.`);
            setLeads(prev => prev.filter(l => l.id !== leadId));
        } catch (error) {
            toast.error(`Failed to delete: ${error.message}`);
        }
    };

    const handleSaveEdit = (updatedLead) => {
        setLeads(prev => prev.map(l => l.id === updatedLead.id ? updatedLead : l));
    };

    const handleAssignTeam = (lead) => {
        if (lead.status.toLowerCase() === 'converted' && lead.case_id) {
            setLeadToAssign(lead);
        } else {
            toast.error('Lead must be converted and have a Case ID before team assignment.');
        }
    };

    // Экран арендатора fitenko живёт СВОИМ файлом. Это сразу и рубеж, и граница
    // измерения цены рисунка — тот же довод, по которому в задаче u2q карточка
    // события выехала в свой файл.
    if (isFitenko) return <FitenkoObrashcheniya />;

    if (isLoading) return <LoadingSpinner message="Loading leads..." />;

    return (
        <>
            {selectedLead && <LeadPreviewModal lead={selectedLead} onClose={() => setSelectedLead(null)} />}
            {editingLead && (
                <LeadEditModal
                    lead={editingLead}
                    onClose={() => setEditingLead(null)}
                    onSave={handleSaveEdit}
                />
            )}
            {leadToAssign && (
                <TeamAssignmentModal
                    caseId={leadToAssign.case_id}
                    caseName={leadToAssign.full_name}
                    onClose={() => setLeadToAssign(null)}
                    onAssignmentSuccess={() => setLeadToAssign(null)}
                />
            )}

            <div className="leads-page-container container">
                <header className="page-header">
                    <h1>Leads Management</h1>
                    <p>Leads from Sofia phone calls and the web chat — review, manage, and convert.</p>
                </header>

                {/* Search & Filter Bar */}
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                    <input
                        className="form-input"
                        style={{ flex: '1', minWidth: '200px', maxWidth: '380px' }}
                        type="text"
                        placeholder="Search by name, email, or phone..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <select
                        className="form-input"
                        style={{ minWidth: '160px' }}
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        {STATUSES.map(s => (
                            <option key={s} value={s}>{s === 'ALL' ? 'All Statuses' : s}</option>
                        ))}
                    </select>
                    <span style={{ display: 'flex', alignItems: 'center', color: '#6b7280', fontSize: '0.9rem' }}>
                        {filteredLeads.length} of {leads.length} leads
                    </span>
                </div>

                <div className="card results-card">
                  <div className="table-scroll">
                    <table className="cases-table">
                        <thead>
                            <tr>
                                <th className="col-name">Full Name</th>
                                <th className="col-email">Email</th>
                                <th className="col-phone">Phone</th>
                                <th className="col-source">Source</th>
                                <th className="col-status">Status</th>
                                <th className="col-date">Created At</th>
                                <th className="col-actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredLeads.length > 0 ? (
                                filteredLeads.map(lead => (
                                    <tr key={lead.id}>
                                        <td className="col-name" data-label="Full Name">{lead.full_name}</td>
                                        <td className="col-email" data-label="Email" title={displayEmail(lead.email)}>{displayEmail(lead.email)}</td>
                                        <td className="col-phone" data-label="Phone">{lead.phone || '—'}</td>
                                        <td className="col-source" data-label="Source">
                                            <span className={`source-badge ${sourceMeta(lead.source).cls}`}>{sourceMeta(lead.source).icon} {sourceMeta(lead.source).label}</span>
                                        </td>
                                        <td className="col-status" data-label="Status">
                                            <span className={`status-badge status-${lead.status.toLowerCase()}`}>{lead.status}</span>
                                        </td>
                                        <td className="col-date" data-label="Created">{new Date(lead.created_at).toLocaleDateString()}</td>
                                        <td className="actions-cell" data-label="Actions">
                                            <button className="btn btn-secondary btn-sm" onClick={() => setSelectedLead(lead)}>
                                                View
                                            </button>
                                            <button className="btn btn-secondary btn-sm" onClick={() => setEditingLead(lead)}>
                                                Edit
                                            </button>
                                            {lead.status.toLowerCase() !== 'converted' ? (
                                                <button className="btn btn-primary btn-sm" onClick={() => handleConvert(lead.id)}>
                                                    Convert to Client
                                                </button>
                                            ) : (
                                                lead.case_id ? (
                                                    <button className="btn btn-success btn-sm" onClick={() => handleAssignTeam(lead)}>
                                                        Assign Team
                                                    </button>
                                                ) : (
                                                    <button className="btn btn-secondary btn-sm" disabled title="Case ID not available yet">
                                                        Assign Team
                                                    </button>
                                                )
                                            )}
                                            <button className="btn btn-danger btn-sm" onClick={() => handleDelete(lead.id, lead.full_name)}>
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="no-results">
                                        {otkazOtveta
                                            ? `Не удалось прочитать ответ сервера: ${otkazOtveta}. Это отказ измерителя, а не «обращений нет».`
                                            : (search || statusFilter !== 'ALL' ? 'No leads match your filters.' : 'No leads found.')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                  </div>
                </div>
            </div>
        </>
    );
};

export default LeadsPage;
